import React, { useState } from 'react';
import StepLayout from '../components/StepLayout';
import TrustpilotCarousel from '../components/TrustpilotCarousel';
import EarningsProgressTracker from '../components/EarningsProgressTracker';
import useEnterKey from '../hooks/useEnterKey';
import { api } from '../api';
import { readStoredAttribution } from '../utils/metaAttribution';
import { readStoredReferralCode } from '../utils/referralAttribution';
import { funnelAttributionPayload } from '../utils/funnelAttribution';

// iter-23: rebuild from operator's Welcome page UI spec.
//
//   - Big heading `Hey there 😀` (52px desktop / 36px mobile, weight 700,
//     letter-spacing -0.01em). Emoji rendered as a native unicode glyph
//     with role=img + aria-label so screen readers don't read it as
//     'grinning face' verbatim.
//   - Subline `Do you want to earn some money?` (22px / 18px, color
//     #666).
//   - CTA row: orange `Let's start` button + faint `press Enter` hint.
//     Pressing Enter anywhere on the page advances (already wired via
//     useEnterKey).
//   - No Trustpilot carousel here per the spec ("clean single-CTA
//     hook"; carousel lives on the credentials page).
//
// The big-heading sizing is overridden inline because the global
// .step-title rule is 48px / 600 — the spec asks for 52px / 700 which
// is intentionally heavier than the rest of the form's titles to make
// the welcome feel like a hook rather than a form step.
export default function Step01Welcome({ step, totalSteps, sessionId, setSessionId, sessionData, updateData, onNext }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGetStarted = async () => {
    setLoading(true);
    setError('');
    try {
      // Phase 4 Meta→GHL: pull captured Meta attribution off
      // sessionStorage and include in the session-create payload so the
      // backend can persist it on takeover_sessions. Resolves to {} if
      // the seller arrived organically (no URL params), which the BE
      // schema accepts as all-optional fields.
      const metaAttribution = readStoredAttribution() || {};
      // Referral program (migration 095): ride the captured ?ref code along so
      // the backend can attribute this takeover to the referrer.
      const referralCode = readStoredReferralCode();
      // Funnel attribution (migration 233). Unlike `referralCode` this is
      // ALWAYS spread, never conditionally omitted: the reason is the payload.
      // A session with no `?fs=` tag must still say WHY it has none, or the
      // unattributable volume goes back to being unknowable — the exact
      // failure this replaces.
      const funnel = funnelAttributionPayload();
      const response = await api.createSession({
        deviceFingerprint: navigator.userAgent,
        userAgent: navigator.userAgent,
        ...metaAttribution,
        ...(referralCode ? { referralCode } : {}),
        ...funnel,
      });
      // PR 4 — BE-side idempotency dedup may rejoin an existing session
      // instead of creating a new one. The `rejoined: true` flag tells
      // us the BE matched our localStorage idempotency key against an
      // in-flight session — in which case the FE shouldn't reset state,
      // it should resume rendering against the existing snapshot. App
      // hydration (App.jsx /getSession) handles that automatically once
      // we set sessionId, so we just log the signal for observability.
      if (response && response.rejoined) {
        // eslint-disable-next-line no-console
        console.log('[takeover-app] BE rejoined existing session via idempotency dedup');
      }
      sessionStorage.setItem('sessionToken', response.sessionToken);
      // Country resolved offline from the seller's IP (BE migration 136). Used to
      // default the phone dial code on Step 2 to where the seller actually is
      // (+1 US / +44 GB) — more reliable than browser locale. Phone-only; the
      // seller still explicitly picks their country on Step 4.
      if (response && response.ipCountry && updateData) {
        updateData({ ipCountry: response.ipCountry });
      }
      setSessionId(response.sessionId);
      onNext();
    } catch (err) {
      setError(err.message || 'Failed to start session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEnterKey(handleGetStarted, { enabled: !loading });

  // Landing redesign (2026-09-25, thread landing-pages-look-better).
  // Filip-approved direction: honest offer + live Trustpilot + minimal
  // PER-SITE identity (not a unified brand). The identity is derived from
  // the domain the seller is actually on, so each funnel site shows its own
  // name without any per-site env or build difference.
  const hostname = (typeof window !== 'undefined' && window.location && window.location.hostname) || '';
  const bareHost = hostname.replace(/^www\./, '');
  const dot = bareHost.lastIndexOf('.');
  const siteLabel = dot > 0 ? bareHost.slice(0, dot) : bareHost;
  const siteTld = dot > 0 ? bareHost.slice(dot) : '';
  const siteMark = (siteLabel || 'v').charAt(0).toUpperCase();

  return (
    // No StepLayout title — the welcome page is a bespoke hook. hideTrustpilot
    // opts out of the global carousel mount; the trust strip below is the
    // welcome page's own, with the live Trustpilot rating.
    <StepLayout title={null} hideTrustpilot>
      <div className="welcome2">
        <div className="welcome2__identity">
          <span className="welcome2__mark" aria-hidden="true">{siteMark}</span>
          <span className="welcome2__wordmark">{siteLabel}<span className="tld">{siteTld}</span></span>
          <span className="welcome2__secure">🔒 Secure</span>
        </div>

        {/* Earnings tracker second, right under the identity (Filip, 2026-09-25:
            "domain at the top, then earnings progress, then the main section, and
            trustpilot below it"). Rendered here rather than at the App level, which
            is suppressed for the welcome step (shouldShowEarningsTracker) so the
            identity can sit ABOVE it. */}
        <EarningsProgressTracker step={step} sessionData={sessionData} />

        <h1 className="welcome2__hero">
          Get paid to rent out <span className="welcome2__accent">your LinkedIn.</span>
        </h1>
        <p className="welcome2__lead">
          Earn $20 in the next 2 minutes for setting it up, then ongoing income. Just stay responsive.
        </p>

        <ul className="welcome2__steps">
          <li><span className="welcome2__stepn" aria-hidden="true">1</span><span>Quick 2-minute setup, no cost to you, ever.</span></li>
          <li><span className="welcome2__stepn" aria-hidden="true">2</span><span>Stay responsive when we message you, that&rsquo;s all it takes to keep earning.</span></li>
        </ul>

        {error && (
          <p style={{ color: 'var(--danger)', fontSize: 12, margin: '0 0 12px' }}>{error}</p>
        )}

        <div className="welcome2__cta cta-row">
          <button
            type="button"
            onClick={handleGetStarted}
            disabled={loading}
            className="welcome-cta"
            style={{
              background: loading ? 'var(--primary-soft)' : '#F26C1F',
              color: '#fff',
              padding: '14px 32px',
              borderRadius: 6,
              border: 'none',
              fontSize: 17,
              fontWeight: 500,
              fontFamily: 'var(--font-sans)',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.85 : 1,
              transition: 'background 0.15s ease',
            }}
            onMouseOver={(e) => { if (!loading) e.currentTarget.style.background = '#DD5E15'; }}
            onMouseOut={(e) => { if (!loading) e.currentTarget.style.background = '#F26C1F'; }}
          >
            {loading ? 'Starting...' : "Let's start"}
          </button>
          <span style={{ fontSize: 14, color: '#111', fontFamily: 'var(--font-sans)' }}>
            press Enter <span style={{ color: '#666' }}>↵</span>
          </span>
        </div>

        <p className="welcome2__safe">🔒 Your login is encrypted and stored in a secure vault.</p>

        {/* Trustpilot LAST, below the main section (Filip's order, 2026-09-25).
            The app's existing review carousel, unlinked ("keep the stars that were
            there previously. dont link to the page"). */}
        <div className="welcome2__trustpilot">
          <TrustpilotCarousel />
        </div>
      </div>
    </StepLayout>
  );
}
